import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, UrlSerializer } from '@angular/router';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';

import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { OpenNativeSettings } from '@awesome-cordova-plugins/open-native-settings/ngx';
import { Zoom } from '@awesome-cordova-plugins/zoom/ngx';
import { LanguageService, MultiTranslateHttpService, SystemLoadService } from '@haleo-frontend/data-access/services';
import {
    EnvironmentUtils,
    FeatureFlagService,
    GlobalErrorHandler,
    HttpBroadcastInterceptor,
    HttpErrorInterceptor
} from '@haleo-frontend/utils';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { IonicStorageModule, Storage } from '@ionic/storage';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import player from 'lottie-web';
import { LottieModule } from 'ngx-lottie';
import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CustomUrlSerializer } from './shared/services/custom-url.serializer';
import { SharedModule } from './shared/shared.module';

export function HttpLoaderFactory(http: HttpClient) {
    return new MultiTranslateHttpService(http, [
        {prefix: './assets/i18n/', suffix: '.json'},
        {prefix: './assets/i18n/features/auth/', suffix: '.json'},
        {prefix: './assets/i18n/features/waiting-room/', suffix: '.json'},
        {prefix: './assets/i18n/features/consent-form/', suffix: '.json'},
        {prefix: './assets/i18n/ui/', suffix: '.json'}
    ]);
}

export function jwtOptionsFactory(storage: any) {
    return {
        tokenGetter: () => storage.get('access_token').then((value: any) => value),
        allowedDomains: [EnvironmentUtils.env.apiDomain],
        skipWhenExpired: true
    };
}

export function LanguageServiceFactory(languageService: LanguageService) {
    return () => languageService.init();
}

export function SystemLoadFactory(systemLoadInterceptor: SystemLoadService) {
    return () => systemLoadInterceptor.check();
}

export function FeatureFlagServiceFactory(featureFlagService: FeatureFlagService) {
    return () => featureFlagService.init();
}

export function EnvironmentUtilsFactory() {
    return () => EnvironmentUtils.env = environment
}

export function StorageDriver() {

    if (EnvironmentUtils.env.testing) {
        return {};
    } else {
        return {driverOrder: ['sqlite', 'localstorage']};
    }
}

export function playerFactory() {
    return player;
}

@NgModule({
    declarations: [
        AppComponent
    ],
    imports: [
        BrowserModule,
        HttpClientModule,
        IonicModule.forRoot({
            scrollPadding: false,
            scrollAssist: false
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        IonicStorageModule.forRoot(StorageDriver()),
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: jwtOptionsFactory,
                deps: [Storage]
            }
        }),
        AppRoutingModule,
        SharedModule,
        LottieModule.forRoot({player: playerFactory})
    ],
    providers: [
        File,
        FileOpener,
        OpenNativeSettings,
        Zoom,
        {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
        {provide: ErrorHandler, useClass: GlobalErrorHandler},
        LanguageService,
        SystemLoadService,
        {provide: APP_INITIALIZER, useFactory: EnvironmentUtilsFactory, multi: true},
        {provide: APP_INITIALIZER, useFactory: LanguageServiceFactory, deps: [LanguageService], multi: true},
        {provide: APP_INITIALIZER, useFactory: SystemLoadFactory, deps: [SystemLoadService], multi: true},
        {provide: APP_INITIALIZER, useFactory: FeatureFlagServiceFactory, deps: [FeatureFlagService], multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: HttpBroadcastInterceptor, multi: true},
        {provide: UrlSerializer, useClass: CustomUrlSerializer}
    ],
    bootstrap: [AppComponent]
})
export class AppModule {

}
